import { useGet } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Progress } from "@/components/ui/progress";
import { Target, Gauge, History, CheckCircle2, XCircle } from "lucide-react";

export function AccuracyPage({ refreshKey, embedded }) {
  const { data, loading } = useGet("/accuracy", refreshKey);
  if (loading || !data) return <div className="text-center text-[#6B7280] py-20 font-mono-num">Loading accuracy…</div>;

  const st = data.stats;
  const hasData = st.total > 0;

  return (
    <div className={embedded ? "" : "fade-up"}>
      {!embedded && <h1 className="font-display text-2xl font-bold mb-1">Model Accuracy</h1>}
      {!embedded && <p className="text-xs text-[#6B7280] mb-4 font-mono-num">Forecast error & learning loop · actual vs predicted</p>}

      <div className="grid grid-cols-3 gap-3 mb-5">
        <StatCard icon={Target} label="Dir. Accuracy" value={hasData ? `${st.direction_accuracy}%` : "—"} color="#10B981" />
        <StatCard icon={Gauge} label="Avg Error" value={hasData ? `${st.avg_pct_error}%` : "—"} color="#F59E0B" />
        <StatCard icon={History} label="Recorded" value={st.total} color="#6366F1" />
      </div>

      {hasData && (
        <div className="glass rounded-2xl p-4 mb-5">
          <div className="flex justify-between text-xs font-mono-num mb-2">
            <span className="text-[#9CA3AF]">Directional hit rate</span>
            <span className="text-[#10B981]">{st.correct}/{st.total}</span>
          </div>
          <Progress value={st.direction_accuracy} className="h-2 bg-white/10" />
        </div>
      )}

      {!hasData ? (
        <div className="glass rounded-2xl p-8 text-center text-sm text-[#6B7280]">
          No outcomes recorded yet. Record an actual release from the Calendar or an indicator
          detail to start training the model.
        </div>
      ) : (
        <div data-testid="accuracy-history" className="flex flex-col gap-2">
          {data.history.map((o) => (
            <div key={o.id} className="glass rounded-2xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                {o.direction_correct
                  ? <CheckCircle2 size={18} className="text-[#10B981] shrink-0" />
                  : <XCircle size={18} className="text-[#EF4444] shrink-0" />}
                <div className="min-w-0">
                  <div className="font-display font-semibold text-sm truncate">{o.indicator_name}</div>
                  <div className="text-[10px] text-[#6B7280] font-mono-num">
                    pred {o.predicted_direction} {fmt(o.predicted_value)} → actual {o.actual_direction} {fmt(o.actual_value)}
                    {o.source === "auto" && <span data-testid="outcome-auto-tag" className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">AUTO · FRED</span>}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono-num text-sm font-bold" style={{ color: o.direction_correct ? "#10B981" : "#EF4444" }}>
                  {o.pct_error}%
                </div>
                <div className="text-[10px] text-[#6B7280] font-mono-num">error</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass rounded-2xl p-4 text-center">
      <Icon size={18} className="mx-auto mb-1" style={{ color }} />
      <div className="font-mono-num text-xl font-bold">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
    </div>
  );
}
